import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { modulesAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const ModuleSelector = ({ 
  open, 
  onClose, 
  onSelectModules, 
  selectedModuleIds = [],
  courseId = null 
}) => {
  const { user } = useAuth();
  const { t } = useTranslation(['courses', 'common']);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModules, setSelectedModules] = useState(selectedModuleIds);
  const [authorFilter, setAuthorFilter] = useState('');
  const [authors, setAuthors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMoreModules, setHasMoreModules] = useState(false);

  // Загрузка модулей
  useEffect(() => {
    if (open) {
      // Сбрасываем состояние при открытии
      setCurrentPage(1);
      setModules([]);
      loadModulesAndAuthors();
    }
  }, [open]);

  // Загрузка авторов только один раз при открытии
  useEffect(() => {
    if (open && authors.length === 0) {
      loadAuthors();
    }
  }, [open, authors.length]);

  const loadModulesAndAuthors = async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Загружаем модули с параметрами поиска
      const params = {
        search: searchTerm,
        author: authorFilter,
        isDraft: false,
        limit: 10,
        page: page
      };
      
      const response = await modulesAPI.getAll(params);
      
      const newModules = response.data.modules || [];
      
      if (append) {
        // Добавляем новые модули к существующим
        setModules(prev => [...prev, ...newModules]);
      } else {
        // Заменяем модули (первая загрузка или новый поиск)
        setModules(newModules);
      }
      
      // Обновляем информацию о пагинации
      setCurrentPage(page);
      setTotalPages(response.data.pagination?.pages || 1);
      setHasMoreModules(page < (response.data.pagination?.pages || 1));
      
    } catch (error) {
      console.error('Ошибка при загрузке модулей:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadAuthors = async () => {
    try {
      // Получаем список пользователей через API
      const response = await usersAPI.getAll({ limit: 100 });
      
      // Фильтруем только пользователей с ролями admin, teacher
      const authors = response.data.users
        ?.filter(user => 
          user.userMeta?.roles?.includes('admin') || 
          user.userMeta?.roles?.includes('teacher')
        )
        ?.map(user => ({
          id: user._id,
          name: user.userMeta?.fullName || user.email
        })) || [];
      
      setAuthors(authors);
    } catch (error) {
      console.error('Ошибка при загрузке авторов:', error);
    }
  };

  // Поиск модулей
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (open) {
        // При изменении поиска сбрасываем на первую страницу
        setCurrentPage(1);
        loadModulesAndAuthors(1, false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, authorFilter]);

  // Функция для загрузки следующих модулей
  const loadMoreModules = () => {
    if (hasMoreModules && !loadingMore) {
      loadModulesAndAuthors(currentPage + 1, true);
    }
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSelectAll = () => {
    const allModuleIds = modules.map(module => module._id);
    setSelectedModules(allModuleIds);
  };

  const handleDeselectAll = () => {
    setSelectedModules([]);
  };

  const handleConfirm = () => {
    onSelectModules(selectedModules);
    onClose();
  };

  const handleCancel = () => {
    setSelectedModules(selectedModuleIds);
    onClose();
  };

  const filteredModules = modules.filter(module => {
    // Исключаем модули, которые уже есть в текущем курсе (только при редактировании)
    if (courseId && module.courses?.some(course => course._id === courseId)) {
      return false;
    }
    return true;
  });

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('courses:moduleSelector.title')}
          </Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Фильтры */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('courses:moduleSelector.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 200 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{t('courses:moduleSelector.authorFilter')}</InputLabel>
            <Select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              label={t('courses:moduleSelector.authorFilter')}
            >
              <MenuItem value="">{t('common:all')}</MenuItem>
              {authors.map(author => (
                <MenuItem key={author.id} value={author.id}>
                  {author.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Действия с выбором */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={filteredModules.length === 0}
          >
            {t('common:selectAll')}
          </Button>
          <Button 
            size="small" 
            onClick={handleDeselectAll}
            disabled={selectedModules.length === 0}
          >
            {t('common:deselectAll')}
          </Button>
          <Chip 
            label={`${selectedModules.length} ${t('courses:moduleSelector.selected')}`}
            color="primary"
            size="small"
          />
        </Box>

        {/* Список модулей */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredModules.length === 0 ? (
                <ListItem>
                  <ListItemText 
                    primary={t('courses:moduleSelector.noModules')}
                    sx={{ textAlign: 'center' }}
                  />
                </ListItem>
              ) : (
                filteredModules.map((module) => (
                  <ListItem key={module._id} dense>
                    <Checkbox
                      checked={selectedModules.includes(module._id)}
                      onChange={() => handleModuleToggle(module._id)}
                    />
                    <ListItemText
                      primary={module.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {module.description}
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                              label={`${module.lessons?.length || 0} ${t('courses:moduleSelector.lessons')}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip 
                              label={`${module.tests?.length || 0} ${t('courses:moduleSelector.tests')}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip 
                              label={module.author?.userMeta?.fullName || module.author?.email}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
            
            {/* Кнопка загрузки дополнительных модулей */}
            {hasMoreModules && filteredModules.length > 0 && (
              <Box display="flex" justifyContent="center" py={2}>
                <Button
                  variant="outlined"
                  onClick={loadMoreModules}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {loadingMore ? t('common:loading') : t('courses:moduleSelector.loadMore')}
                </Button>
              </Box>
            )}
            
            {/* Информация о пагинации */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" py={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('courses:moduleSelector.pageInfo', { 
                    current: currentPage, 
                    total: totalPages,
                    count: filteredModules.length 
                  })}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>
          {t('common:actions.cancel')}
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          startIcon={<AddIcon />}
          disabled={selectedModules.length === 0}
        >
          {t('courses:moduleSelector.addSelected', { count: selectedModules.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleSelector;
